import { yupResolver } from "@hookform/resolvers/yup";
import { Save } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import * as yup from "yup";

import { Button } from "@krea/shared/design-system/Button";
import { Card, CardContent } from "@krea/shared/design-system/Card";
import Checkbox from "@krea/shared/design-system/fields/Checkbox";
import { ControlledWrapper } from "@krea/shared/design-system/input/controlled-wrapper";
import { Label } from "@krea/shared/design-system/Label";
import { Separator } from "@krea/shared/design-system/Separator";
import { toast } from "@krea/shared/design-system/utils/toast";

import { LenderUserRoleEnum } from "@/features/common/enums";
import type { IUserSettings } from "@/features/lender-companies/api/types";

const schema = yup.object().shape({
  receiveFactoringEmails: yup.boolean().required(),
  receiveCompanyLoanEmails: yup.boolean().required(),
  receiveRealEstateEmails: yup.boolean().required(),
  receiveLeasingEmails: yup.boolean().required(),
  receiveEmailsOnlyOnSelectedAndLater: yup.boolean().required(),
  receiveApiEmails: yup.boolean().required(),
});

type NotificationSettingsFormValues = yup.InferType<typeof schema>;

interface INotificationsSectionProps {
  settings: IUserSettings;
  onUpdateUserSettings: (data: NotificationSettingsFormValues) => void;
}

export const NotificationsSection = ({
  settings,
  onUpdateUserSettings,
}: INotificationsSectionProps) => {
  const form = useForm<NotificationSettingsFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      receiveFactoringEmails: false,
      receiveCompanyLoanEmails: false,
      receiveRealEstateEmails: false,
      receiveLeasingEmails: false,
      receiveEmailsOnlyOnSelectedAndLater: false,
      receiveApiEmails: false,
    },
  });
  const { setValue, handleSubmit } = form;

  const watch = useWatch({
    control: form.control,
  });

  const shouldShowApiEmailsOption = useMemo(() => {
    return (
      settings?.role &&
      [LenderUserRoleEnum.DEVELOPER, LenderUserRoleEnum.ADMIN].includes(
        settings?.role,
      )
    );
  }, [settings]);

  const isAllEmailChecked = useMemo(() => {
    return Object.values(watch).every(Boolean);
  }, [watch]);

  useEffect(() => {
    const initialValues = {
      receiveFactoringEmails: settings.receiveFactoringEmails,
      receiveCompanyLoanEmails: settings.receiveCompanyLoanEmails,
      receiveRealEstateEmails: settings.receiveRealEstateEmails,
      receiveLeasingEmails: settings.receiveLeasingEmails,
      receiveEmailsOnlyOnSelectedAndLater:
        settings.receiveEmailsOnlyOnSelectedAndLater,
      receiveApiEmails: settings.receiveApiEmails,
    };
    form.reset(initialValues);
  }, [settings, form]);

  const setAllEmail = (checked: boolean) => {
    setValue("receiveFactoringEmails", checked);
    setValue("receiveCompanyLoanEmails", checked);
    setValue("receiveRealEstateEmails", checked);
    setValue("receiveLeasingEmails", checked);
    setValue("receiveEmailsOnlyOnSelectedAndLater", checked);

    if (shouldShowApiEmailsOption) setValue("receiveApiEmails", checked);
  };

  const onSubmit = useCallback(() => {
    handleSubmit((data) => {
      onUpdateUserSettings(data);
      toast.success("Notification settings updated successfully");
    })();
  }, [handleSubmit, onUpdateUserSettings]);

  return (
    <Card>
      <CardContent className="space-y-6">
        <h1 className="text-section">Notification Settings</h1>

        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-primary-150 p-4">
            <div className="flex items-center justify-between">
              <Label className="flex items-center">
                <Checkbox
                  checked={isAllEmailChecked}
                  onChange={(checked) => setAllEmail(checked)}
                />
                <span className="text-base-strong">
                  All Email Notifications
                </span>
              </Label>
              <span className="rounded-sm border border-gray-200 bg-white px-2 py-1 text-label">
                Master Control
              </span>
            </div>

            <Separator className="my-3" />

            <div className="space-y-3">
              <ControlledWrapper
                control={form.control}
                name="receiveFactoringEmails"
                render={({ field }) => (
                  <div className="flex items-center">
                    <Checkbox
                      checked={field.value}
                      onChange={(checked) => field.onChange(checked)}
                    />
                    <span className="block text-label">
                      Receive factoring emails
                    </span>
                  </div>
                )}
              />

              <ControlledWrapper
                control={form.control}
                name="receiveCompanyLoanEmails"
                render={({ field }) => (
                  <div className="flex items-center">
                    <Checkbox
                      checked={field.value}
                      onChange={(checked) => field.onChange(checked)}
                    />
                    <span className="block text-label">
                      Receive company loan emails
                    </span>
                  </div>
                )}
              />

              <ControlledWrapper
                control={form.control}
                name="receiveRealEstateEmails"
                render={({ field }) => (
                  <div className="flex items-center">
                    <Checkbox
                      checked={field.value}
                      onChange={(checked) => field.onChange(checked)}
                    />
                    <span className="block text-label">
                      Receive real estate emails
                    </span>
                  </div>
                )}
              />

              <ControlledWrapper
                control={form.control}
                name="receiveLeasingEmails"
                render={({ field }) => (
                  <div className="flex items-center">
                    <Checkbox
                      checked={field.value}
                      onChange={(checked) => field.onChange(checked)}
                    />
                    <span className="block text-label">
                      Receive leasing emails
                    </span>
                  </div>
                )}
              />

              <ControlledWrapper
                control={form.control}
                name="receiveEmailsOnlyOnSelectedAndLater"
                render={({ field }) => (
                  <div className="flex items-center">
                    <Checkbox
                      checked={field.value}
                      onChange={(checked) => field.onChange(checked)}
                    />
                    <span className="block text-label">
                      Receive emails only on selected and later
                    </span>
                  </div>
                )}
              />

              {shouldShowApiEmailsOption && (
                <ControlledWrapper
                  control={form.control}
                  name="receiveApiEmails"
                  render={({ field }) => (
                    <div className="flex items-center">
                      <Checkbox
                        checked={field.value}
                        onChange={(checked) => field.onChange(checked)}
                      />
                      <span className="block text-label">
                        Receive API emails (only for developers and admins)
                      </span>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>

        <Separator />
        <div className="flex justify-end">
          <Button type="button" onClick={onSubmit}>
            <Save className="mr-2 h-4 w-4" />
            Save Notification Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
